import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { TextReveal } from '../components/ui/TextReveal';
import { ProjectModal } from '../components/ui/ProjectModal';
import { MaskReveal } from '../components/ui/MaskReveal';
const categories = ['Alle', 'Software', 'Design', 'Video', 'Beratung'];
interface Project {
  title: string;
  industry: string;
  category: string;
  description: string;
  result: string;
  technologies: string[];
  challenge: string;
  solution: string;
  fullDescription: string;
  image: string;
  codeSnippet: string;
}
const projects: Project[] = [{
  title: 'Digitale Plattform für Logistikunternehmen',
  industry: 'Logistik & Transport',
  category: 'Software',
  description: 'Entwicklung einer zentralen Plattform zur Verwaltung von Aufträgen, Fahrzeugen und Kundenbeziehungen.',
  result: '40% Effizienzsteigerung',
  technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
  challenge: 'Das Unternehmen arbeitete mit fragmentierten Systemen: Excel-Tabellen für Aufträge, separate Software für Flottenmanagement und manuelle Kundenkommunikation.',
  solution: 'Wir entwickelten eine zentrale Plattform, die alle Prozesse vereint. Echtzeit-Tracking, automatisierte Auftragsverteilung und ein Kundenportal für Self-Service.',
  fullDescription: 'Für einen mittelständischen Logistikdienstleister mit über 200 Fahrzeugen entwickelten wir eine maßgeschneiderte Plattform.',
  image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80',
  codeSnippet: 'await deploy(config)'
}, {
  title: 'Markenrelaunch für Maschinenbauer',
  industry: 'Industrie & Fertigung',
  category: 'Design',
  description: 'Komplette Neugestaltung der Markenidentität inklusive Logo, Corporate Design und Kommunikationsmaterialien.',
  result: 'Erfolgreiche Neupositionierung',
  technologies: ['Figma', 'Adobe CC', 'Webflow'],
  challenge: 'Die bestehende Markenidentität stammte aus den 1990er Jahren und spiegelte nicht mehr die Innovationskraft wider.',
  solution: 'Wir entwickelten eine zeitgemäße Markenidentität, die Tradition und Innovation verbindet.',
  fullDescription: 'Ein traditionsreicher Maschinenbauer mit 150 Jahren Geschichte benötigte eine Markenidentität.',
  image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
  codeSnippet: 'const brand = redesign()'
}, {
  title: 'Imagefilm für Technologiekonzern',
  industry: 'Technologie',
  category: 'Video',
  description: 'Emotionaler Unternehmensfilm zur Darstellung von Innovation, Qualität und Unternehmenskultur.',
  result: '2,5 Mio. Views',
  technologies: ['RED Cinema', 'DaVinci Resolve', 'After Effects'],
  challenge: 'Das Unternehmen hatte Schwierigkeiten, qualifizierte Fachkräfte zu gewinnen.',
  solution: 'Wir produzierten einen emotionalen Imagefilm, der die Menschen hinter der Technologie in den Mittelpunkt stellt.',
  fullDescription: 'Für einen führenden Technologiekonzern produzierten wir einen Imagefilm.',
  image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80',
  codeSnippet: 'render(<Film />)'
}, {
  title: 'Cloud-Migration für Finanzdienstleister',
  industry: 'Finanzwesen',
  category: 'Software',
  description: 'Sichere Migration kritischer Systeme in eine moderne Cloud-Infrastruktur mit Zero-Downtime.',
  result: '99,9% Verfügbarkeit',
  technologies: ['AWS', 'Terraform', 'Docker', 'Kubernetes'],
  challenge: 'Die veraltete On-Premise-Infrastruktur verursachte hohe Wartungskosten.',
  solution: 'Wir führten eine schrittweise Migration in eine sichere Cloud-Umgebung durch.',
  fullDescription: 'Ein etablierter Finanzdienstleister benötigte eine moderne, skalierbare Infrastruktur.',
  image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
  codeSnippet: 'terraform apply'
}, {
  title: 'KI-gestützte Prozessoptimierung',
  industry: 'Produktion',
  category: 'Beratung',
  description: 'Analyse und Automatisierung von Produktionsprozessen durch Machine Learning.',
  result: '30% Kosteneinsparung',
  technologies: ['Python', 'TensorFlow', 'Power BI'],
  challenge: 'Manuelle Qualitätskontrollen und reaktive Wartung führten zu hohen Ausschussraten.',
  solution: 'Wir implementierten ein KI-System, das Produktionsdaten in Echtzeit analysiert.',
  fullDescription: 'Für einen Produktionsbetrieb entwickelten wir eine KI-gestützte Lösung.',
  image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&q=80',
  codeSnippet: 'model.predict(data)'
}, {
  title: 'E-Commerce-Plattform für B2B-Handel',
  industry: 'Handel & Distribution',
  category: 'Software',
  description: 'Entwicklung eines B2B-Shops mit komplexer Preislogik und ERP-Integration.',
  result: '+85% Online-Umsatz',
  technologies: ['React', 'Shopify Plus', 'SAP Integration'],
  challenge: 'Der Vertrieb lief ausschließlich über Außendienst und Telefon.',
  solution: 'Wir entwickelten eine B2B-Plattform mit kundenspezifischen Preisen.',
  fullDescription: 'Ein Großhändler für technische Produkte benötigte eine digitale Vertriebsplattform.',
  image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80',
  codeSnippet: 'checkout.process()'
}];
export function ProjectsPage() {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const filteredProjects = activeCategory === 'Alle' ? projects : projects.filter(p => p.category === activeCategory);
  return <div className="w-full" data-id="element-496">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-497">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-498">
          <AnimatedSection data-id="element-499">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest gradient-text max-w-3xl" data-id="element-500">
              Projekte & Referenzen
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.1} data-id="element-501">
            <p className="mt-6 text-lg md:text-xl text-white/50 max-w-2xl" data-id="element-502">
              Einblicke in erfolgreiche Zusammenarbeit mit unseren Kunden. Jedes
              Projekt erzählt eine Geschichte.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Filter & Projects */}
      <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-503">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-504">
          {/* Filter Tabs */}
          <AnimatedSection data-id="element-505">
            <div className="flex flex-wrap gap-2 mb-16" data-id="element-506">
              {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeCategory === category ? 'bg-[#E8781A] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`} data-id="element-507">
                  {category}
                </button>)}
            </div>
          </AnimatedSection>

          {/* Projects Grid */}
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" layout data-id="element-508">
            <AnimatePresence mode="popLayout" data-id="element-509">
              {filteredProjects.map(project => <motion.div key={project.title} layout layoutId={project.title} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.3
            }} className="group cursor-pointer" onClick={() => setSelectedProject(project)} data-id="element-510">
                  <MaskReveal className="aspect-[4/3] rounded-2xl overflow-hidden mb-6" direction="up" data-id="element-511">
                    <div className="relative w-full h-full" data-id="element-512">
                      <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-513" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-514">
                        <div className="text-center" data-id="element-515">
                          <div className="font-mono text-xs text-[#E8781A]/80 bg-black/50 px-4 py-2 rounded-lg mb-3" data-id="element-516">
                            {project.codeSnippet}
                          </div>
                          <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-sm font-medium text-white" data-id="element-517">
                            Details ansehen
                          </span>
                        </div>
                      </div>
                    </div>
                  </MaskReveal>
                  <div className="flex items-center gap-3 mb-3" data-id="element-518">
                    <span className="text-xs font-medium uppercase tracking-wider text-white/40" data-id="element-519">
                      {project.industry}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-520" />
                    <span className="text-xs font-medium text-[#E8781A]" data-id="element-521">
                      {project.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-[#E8781A] transition-colors" data-id="element-522">
                    {project.title}
                  </h3>
                  <p className="mt-2 text-white/50" data-id="element-523">{project.description}</p>
                  <div className="mt-4 inline-flex items-center px-3 py-1.5 bg-white/5 rounded-lg" data-id="element-524">
                    <span className="text-sm font-medium text-white" data-id="element-525">
                      {project.result}
                    </span>
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#E8781A]/10 via-[#09090B] to-[#a855f7]/10 border-y border-white/5" data-id="element-526">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-527">
          <AnimatedSection data-id="element-528">
            <div className="max-w-3xl mx-auto text-center" data-id="element-529">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest text-white" data-id="element-530">
                Ihr Projekt könnte das nächste sein.
              </h2>
              <p className="mt-6 text-lg text-white/50" data-id="element-531">
                Lassen Sie uns gemeinsam herausfinden, wie wir Ihre Vision
                Realität werden lassen.
              </p>
              <div className="mt-10" data-id="element-532">
                <Link to="/kontakt" className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#09090B] font-medium rounded-lg hover:bg-white/90 transition-colors" data-id="element-533">
                  Projekt besprechen
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-534" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <ProjectModal project={selectedProject} isOpen={!!selectedProject} onClose={() => setSelectedProject(null)} data-id="element-535" />
    </div>;
}